import * as Sentry from '@sentry/react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { isSessionFailure } from '../../auth/errors';
import { msalInstance } from '../../auth/msalConfig';

/**
 * What the router shows when a loader or a route component throws.
 *
 * Without one, TanStack renders its own fallback — an unstyled "Something went
 * wrong!" over the raw message — and the most ordinary failure there is reached
 * it: a silent renewal that could not complete. `getIdToken` rethrows anything
 * that is not an `InteractionRequiredAuthError` (deliberately: a dropped
 * connection must not navigate the whole SPA to Entra and cost an operator an
 * uncommitted crop), the hey-api adapter leaves an error with no response
 * untyped, and `_auth`'s loader only knows how to answer an `ApiError`. So the
 * operator sat on LOADING for the length of MSAL's iframe timeout and was then
 * handed a stack trace.
 *
 * A renewal failure is ambiguous — a blocked iframe, an unreachable Entra, a
 * revoked session all land here — so this does not choose for the operator.
 * It says what happened and offers both ways out.
 */
export function RouteErrorPage({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const session = isSessionFailure(error);

  useEffect(() => {
    // A dead session is not a defect and would only be noise in Sentry.
    if (!session) {
      Sentry.captureException(error);
    }
  }, [error, session]);

  function retry() {
    reset();
    void router.invalidate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="epi-card tone-blue max-w-md text-center">
        <p className="label dash mb-3 text-left">
          {session ? 'SESSION' : 'RUNTIME ERROR'}
        </p>
        <h1 className="display mb-4 text-[36px]">
          {session ? t('errors.session.title') : t('errors.unexpected.title')}
        </h1>
        <p className="mb-6 text-muted">
          {session ? t('errors.session.body') : t('errors.unexpected.body')}
        </p>
        <div className="flex items-center justify-center gap-3">
          <button onClick={retry} className="btn-blue">
            {t('common.retry')}
          </button>
          {session ? (
            <button
              onClick={() => void msalInstance.logoutRedirect()}
              className="btn-ghost"
            >
              {t('common.signOut')}
            </button>
          ) : (
            <button
              onClick={() => window.location.reload()}
              className="btn-ghost"
            >
              {t('common.refresh')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
